/**
 * Cross-area references for the supplier 360 page: the invoice-compliance
 * case and the risk alerts that belong to each supplier. Ids and amounts are
 * the canonical ones from the demo README; the compliance and risk pages own
 * the full records.
 */
import type { SupplierId } from '@/demo/procurement/data/types';

export type InvoiceCaseStatus = 'violation' | 'warning' | 'compliant';

export interface InvoiceCase {
  invoiceId: string;
  status: InvoiceCaseStatus;
  /** Invoice total, USD. */
  amount: number;
  /** One line for the supplier page. */
  summary: string;
}

export const INVOICE_CASES: Partial<Record<SupplierId, InvoiceCase>> = {
  'sup-corvane': {
    invoiceId: 'CV-2026-08812',
    status: 'violation',
    amount: 305_900,
    summary:
      'Net 30 billed against Net 45, a unit price above the price increase cap and freight billed on a contract that already includes freight. $8,848 recoverable.',
  },
  'sup-lumen': {
    invoiceId: 'LB-26-3391',
    status: 'violation',
    amount: 412_500,
    summary:
      'An $82,500 tariff surcharge line, although §5.3 puts all duties and tariffs on the supplier.',
  },
  'sup-halbrook': {
    invoiceId: 'HP-INV-55102',
    status: 'violation',
    amount: 96_330,
    summary:
      'A Senior Partner rate that is not in the agreed hourly rates, expenses above the 10% cap, and billing that is still $10,420 over the budget cap after those come off. $18,200 recoverable.',
  },
  'sup-transcon': {
    invoiceId: 'TCF-778120',
    status: 'violation',
    amount: 60_670,
    summary:
      'Fuel surcharge at 24% against the 18% cap, and waiting-time charges billed without signed proof. $4,030 recoverable.',
  },
  'sup-stratus': {
    invoiceId: 'STR-INV-20931',
    status: 'warning',
    amount: 1_000_000,
    summary:
      'September uptime was 99.81% against the 99.95% service level. A 10% credit of $100,000 is owed and not applied.',
  },
  'sup-keystone': {
    invoiceId: 'KS-104477',
    status: 'warning',
    amount: 42_318,
    summary:
      'A 38% markup against the 32% cap, and overtime billed without approval. $2,460 recoverable.',
  },
  'sup-northgate': {
    invoiceId: 'NGF-22871',
    status: 'warning',
    amount: 64_200,
    summary:
      'Year-to-date volume passed $8M, so the 1.5% rebate on all purchases is owed and has not been credited. $123,600 owed.',
  },
  'sup-gently': {
    invoiceId: 'GAI-2026-0912',
    status: 'compliant',
    amount: 30_000,
    summary:
      'Monthly subscription invoice. Net 45 terms match the master agreement.',
  },
  'sup-aurelia': {
    invoiceId: 'AFP-90233',
    status: 'compliant',
    amount: 186_400,
    summary:
      'Compliant. A 2% early-pay discount worth $3,728 is available if paid within 10 days.',
  },
  'sup-brightline': {
    invoiceId: 'BLC-3318',
    status: 'compliant',
    amount: 125_000,
    summary: 'Milestone 2 billing matches the accepted deliverable.',
  },
};

export type AlertSeverity = 'critical' | 'high' | 'medium';

export interface AlertRef {
  id: string;
  severity: AlertSeverity;
  title: string;
}

export const ALERTS: Partial<Record<SupplierId, AlertRef[]>> = {
  'sup-vitrex': [
    {
      id: 'RSK-301',
      severity: 'critical',
      title:
        'Quality problem at Vitrex: lot VX-24117 built at a factory UL has not certified',
    },
  ],
  'sup-lumen': [
    {
      id: 'RSK-305',
      severity: 'critical',
      title:
        'Lumen Drive, the sole 50 HP motor drive source, is billing a 25% tariff surcharge the agreement forbids',
    },
  ],
  'sup-brightpak': [
    {
      id: 'RSK-307',
      severity: 'critical',
      title:
        'The aluminium supplier behind Brightpak is a possible match on the forced-labor import ban list',
    },
  ],
  'sup-corvane': [
    {
      id: 'RSK-298',
      severity: 'high',
      title:
        'Corvane credit score downgraded from 72 to 58 after parent refinancing',
    },
  ],
  'sup-stellar': [
    {
      id: 'RSK-296',
      severity: 'high',
      title:
        'A new energy-efficiency rule and a steel shortage put Stellar Transformer capacity at risk',
    },
  ],
  'sup-stratus': [
    {
      id: 'RSK-303',
      severity: 'high',
      title:
        'Stratus discloses a security incident in its support tooling environment',
    },
  ],
  'sup-kaisei': [
    {
      id: 'RSK-299',
      severity: 'high',
      title:
        'Kaisei ships from a single plant in an earthquake zone with an outdated continuity plan',
    },
  ],
  'sup-polaris': [
    {
      id: 'RSK-300',
      severity: 'high',
      title: 'Polaris is the only source for watertight enclosures',
    },
  ],
  'sup-transcon': [
    {
      id: 'RSK-302',
      severity: 'high',
      title: 'Transcontinental accepted only 82% of Memphis shipments',
    },
  ],
  'sup-keystone': [
    {
      id: 'RSK-304',
      severity: 'high',
      title:
        'California wage change will reach Keystone temp rates at Hayward DC',
    },
  ],
  'sup-meridian': [
    {
      id: 'RSK-297',
      severity: 'medium',
      title:
        'PVC resin market price up 11% in 30 days ahead of the Meridian price reset',
    },
  ],
  'sup-northgate': [
    {
      id: 'RSK-306',
      severity: 'medium',
      title:
        'Northgate ISO 9001 certificate expires in 23 days with no audit scheduled',
    },
  ],
};

export function alertsForSupplier(supplierId: SupplierId): AlertRef[] {
  return ALERTS[supplierId] ?? [];
}

export function invoiceCaseFor(
  supplierId: SupplierId,
): InvoiceCase | undefined {
  return INVOICE_CASES[supplierId];
}

/** The sourcing event a supplier is part of, for deep links from the agreements tab. */
export const SOURCING_EVENTS: Partial<Record<SupplierId, string>> = {
  'sup-brightpak': 'RFP-2026-014',
  'sup-hanover': 'RFP-2026-014',
  'sup-aurelia': 'RFP-2026-014',
  'sup-northgate': 'RFP-2026-014',
  'sup-kestrel': 'RFQ-2026-022',
};
